import { AlertTriangle, X } from 'lucide-react';
import type { MemberUnavailability, BandMember } from '../../types/database';

interface Props {
  conflicts: MemberUnavailability[];
  members: BandMember[];
  eventDate: string;
  onCancel: () => void;
  onProceed: () => void;
}

export default function UnavailabilityConflictModal({ conflicts, members, eventDate, onCancel, onProceed }: Props) {
  const formattedDate = new Date(eventDate + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Resolve member names and instruments from the conflicts
  const conflictDetails = conflicts.map((c) => {
    const member = members.find((m) => m.user_id === c.user_id);
    const name = c.profile?.display_name || member?.display_name || member?.email || c.profile?.email || 'Unknown member';
    const instruments = member?.instruments ?? [];
    return { name, instruments, reason: c.reason };
  });

  // Deduplicate by user (a member might have overlapping unavailability entries)
  const uniqueConflicts = conflictDetails.filter(
    (c, i, arr) => arr.findIndex((x) => x.name === c.name) === i
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-900 border border-amber-500/40 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Availability Conflict</h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-gray-300 text-sm">
            The following band member{uniqueConflicts.length > 1 ? 's are' : ' is'} unavailable on{' '}
            <span className="font-semibold text-gray-50">{formattedDate}</span>:
          </p>

          <div className="space-y-2">
            {uniqueConflicts.map((c, i) => (
              <div key={i} className="bg-amber-900/20 border border-amber-500/20 rounded-lg px-4 py-3">
                <div className="font-medium text-gray-50">{c.name}</div>
                {c.instruments.length > 0 && (
                  <div className="text-sm text-amber-300 mt-0.5">
                    {c.instruments.join(', ')}
                  </div>
                )}
                {c.reason && (
                  <div className="text-sm text-gray-400 mt-0.5">
                    Reason: {c.reason}
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-gray-400 text-sm">
            You can still add this event if you have a dep available to cover.
          </p>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg transition"
            >
              Go Back
            </button>
            <button
              type="button"
              onClick={onProceed}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2 px-4 rounded-lg font-medium transition"
            >
              Add Anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
